import {AfterViewInit, ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, Output} from '@angular/core';
import {DistributionAccount, TargetSystemId} from '../../../../distribution-account/distribution-account.model';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {debounceTime, distinctUntilChanged, map, startWith, tap} from 'rxjs/operators';
import {SubSink} from 'subsink';
import {PubmaticBuyer, PubmaticViewModel} from './pubmatic-state';
import {BehaviorSubject, combineLatest} from 'rxjs';
import {PubmaticFacadeService} from './pubmatic-facade.service';
import {BuyerChange} from './buyers-edit/buyers-edit.component';
import {PubmaticEditConfig} from './account-pubmatic-edit-wrapper.component';
import {PubmaticShareConfig} from '../../../../segment-multi-share/segment-share.service';


@Component({
  selector: 'app-account-pubmatic',
  template: `
    <div class="logo"
         [class.pubmatic-crtt-logo]="target===targetId.PUBMATIC_CRTT"
         [class.pubmatic-logo]="target===targetId.PUBMATIC"
    ></div>
    <form [formGroup]="form">
      <mat-card>
        <mat-card-title>Price</mat-card-title>
        <mat-card-subtitle>Required, in dollars</mat-card-subtitle>
        <mat-card-content>
          <mat-form-field class="form-field-price-input">
            <input class="price-input" formControlName="price" matInput name="price" step="0.01"
                   type="number"/>
            <span matSuffix>&nbsp;$</span>
            <mat-error *ngIf="form.controls['price'].invalid">You must enter a value</mat-error>
          </mat-form-field>
        </mat-card-content>
      </mat-card>

      <mat-card>
        <mat-card-title>Buyers</mat-card-title>
        <mat-card-content>
          <mat-card-subtitle>
            PMP Pubmatic buyers
            <mat-icon
              [matTooltip]="getTooltipInfo()">info
            </mat-icon>
          </mat-card-subtitle>
          <app-buyers-edit [buyersSource]="facade.selectBuyers()"
                           [selectedBuyers]="selectedBuyers$"
                           (buyerChange)="buyerChange($event)"></app-buyers-edit>
        </mat-card-content>
      </mat-card>
    </form>
  `,
  styleUrls: ['./account-pubmatic.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountPubmaticComponent implements AfterViewInit, OnDestroy {
  @Input() account!: DistributionAccount;
  @Input() target!: number;
  @Output() configChange: EventEmitter<PubmaticEditConfig> = new EventEmitter<PubmaticEditConfig>();
  form = new FormGroup({
    price: new FormControl('0.2', Validators.required),
  });
  targetId = TargetSystemId;
  pubModel?: PubmaticViewModel;
  selectedBuyers$ = new BehaviorSubject<PubmaticBuyer[]>([]);
  private subSink = new SubSink();

  constructor(public facade: PubmaticFacadeService) {
  }

  @Input() set pubmaticModel(pubmaticModel: PubmaticViewModel) {
    this.pubModel = pubmaticModel;
    if (pubmaticModel) {
      this.form.controls['price'].setValue(pubmaticModel.price);
      this.selectedBuyers$.next(pubmaticModel.buyers);
    }
  }

  getTooltipInfo(): string {
    return 'ID: -1 means sharing segment to ALL Pubmetic buyers. NOTE: In order to save changes, ' +
      'remember to click \'Save changes\' button!';
  }

  buyerChange(buyer: BuyerChange) {
    const copyOfSelectedbuyers = [...this.selectedBuyers$.value];
    if (buyer.type === 'REMOVE') {
      this.selectedBuyers$.next(copyOfSelectedbuyers.filter(selected => selected !== buyer.payload));
    } else {
      if (!copyOfSelectedbuyers.includes(buyer.payload)) {
        this.selectedBuyers$.next([...copyOfSelectedbuyers, buyer.payload]);
      }
    }
  }

  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  ngAfterViewInit(): void {
    this.listenToChangesAndEmit();
  }

  private listenToChangesAndEmit() {
    const debouncedFormValueChanges = this.form.valueChanges
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        map(change => change.price),
        startWith(this.getDefaultPriceValue()),
      );

    this.subSink.sink = combineLatest([debouncedFormValueChanges, this.selectedBuyers$])
      .pipe(
        tap(([price, buyers]) => {
          this.configChange.emit({
            accountId: this.account.id,
            price,
            buyers
          } as PubmaticEditConfig);
        }))
      .subscribe();
  }

  private getDefaultPriceValue() {
    if (this.pubModel) {
      return this.pubModel.price;
    } else {
      return '0.20';
    }
  };
}
